import React, { useCallback, useMemo, useState } from 'react';
import { Box, Grid, SearchInput, useDebounce, VirtualTable } from '@common-fe/common-fe';

import { FilterSetters } from '@/utils/hooks/useFilters';

import { ReportTab, ReportTableBaseProps, ReportTypeKeys } from '../../report.types';
import { AddScheduledReportButton } from '../../ScheduledReport';
import EmployerSelector from '../EmployerSelector';
import EmploymentStatusDropdown from '../EmploymentStatusDropdown';
import ExportButton from '../ExportButton';
import {
  useIsEmployerIsPresent,
  useMultipleEmployerSelectAvailable,
  useShowAppliedSettingsSnackbar,
} from '../hooks';
import MultipleEmployerSelector from '../MultipleEmployerSelector';
import PlaceholderBase from '../PlaceholderBase';
import ReportLastUpdateDate from '../ReportLastUpdateDate';
import ReportTableWrapper from '../ReportTable/ReportTableWrapper';
import ToggleSSNVisible from '../ToggleSSNVisible';
import TopButtonPortalWrapper from '../TopButtonPortalWrapper';

import { CardStatusReportTableParams } from './CardStatusReport.types';
import CardStatusSelector from './CardStatusSelector';
import MailedDatePicker from './MailedDatePicker';
import { useCardStatusReportExportCSVQuery, useCardStatusReportQuery } from './queries';

interface Props extends ReportTableBaseProps {
  filtersValues: CardStatusReportTableParams;
  onSetFilterValue: FilterSetters<CardStatusReportTableParams>;
}

const CardStatusReportTable: React.FC<Props> = ({
  filtersValues,
  onSetFilterValue,
  isPreviewMode,
  defaultEmployerId,
}) => {
  const isEmployerIsPresent = useIsEmployerIsPresent(filtersValues, {
    isMultipleEmployerMode: true,
  });
  const isMultipleEmployerAvailable = useMultipleEmployerSelectAvailable();
  const [isAutoFocus, setAutoFocus] = useState(false);
  const debouncedSearch = useDebounce(filtersValues.searchString);
  const {
    isHasNextPage,
    total,
    isLoading,
    headers,
    nodes,
    lastTimeUpdateDate,
    columnWidths,
    isExportDisabled,
    handleLoadMore,
    onChangeHeaderSize,
  } = useCardStatusReportQuery({
    ...filtersValues,
    isMultipleEmployer: isMultipleEmployerAvailable,
    isPreviewMode,
    searchString: debouncedSearch,
  });

  const childrenNode = useMemo(() => {
    if (!isEmployerIsPresent) {
      return (
        <PlaceholderBase>
          <PlaceholderBase.Link onClick={() => setAutoFocus(true)} />
        </PlaceholderBase>
      );
    }

    if (!isLoading && !total) {
      if (filtersValues.searchString.trim()) {
        return (
          <PlaceholderBase isLarge>
            <PlaceholderBase.Search />
          </PlaceholderBase>
        );
      }
      return (
        <PlaceholderBase isLarge>
          <PlaceholderBase.Default />
        </PlaceholderBase>
      );
    }
    return (
      <VirtualTable
        testId="report-table"
        height={isPreviewMode ? 'calc(50vh)' : 'calc(80vh - 160px)'}
        isLoading={isLoading}
        onResizeHeaders={onChangeHeaderSize}
        onLoadMore={handleLoadMore}
        isHasMore={isHasNextPage}
        columnSizes={columnWidths}
        isIndexMode={true}
        headers={headers}
        items={nodes}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPreviewMode,
    columnWidths,
    filtersValues.employerIds,
    filtersValues.employerId,
    filtersValues.searchString,
    handleLoadMore,
    onChangeHeaderSize,
    headers,
    isHasNextPage,
    isLoading,
    nodes,
    total,
  ]);
  const { handleExport: handleExportCSV, isLoading: isExportLoading } = useCardStatusReportExportCSVQuery({
    ...filtersValues,
    isMultipleEmployer: isMultipleEmployerAvailable,
  });

  const handleShowAppliedFilterSnackbar = useShowAppliedSettingsSnackbar();
  const handleToggleSSN = useCallback(
    (value: boolean) => {
      onSetFilterValue.isSSNShowed(value);
      handleShowAppliedFilterSnackbar();
    },
    [handleShowAppliedFilterSnackbar, onSetFilterValue]
  );

  return (
    <Box direction="column">
      <TopButtonPortalWrapper
        isPreviewMode={isPreviewMode}
        infoNode={
          <ReportLastUpdateDate
            isWideMode
            isLoading={isLoading}
            key={`${filtersValues.employerId}`}
            value={lastTimeUpdateDate}
          />
        }
        buttonNode={
          <Box direction="row">
            <AddScheduledReportButton
              employerId={filtersValues.employerId}
              employerIds={filtersValues.employerIds}
              isMultipleEmployer={isMultipleEmployerAvailable}
              type={ReportTypeKeys.CardStatus}
              tab={ReportTab.Report}
            />
            <Box margin={{ left: 'spacing12' }}>
              <ExportButton
                isLoading={isExportLoading}
                onClick={handleExportCSV}
                disabled={!isEmployerIsPresent || isExportDisabled}
                text="Download Report (CSV)"
              />
            </Box>
          </Box>
        }
      >
        <ReportTableWrapper
          total={total}
          isLoading={isLoading}
          isPreviewMode={isPreviewMode}
          headerChildren={
            <ToggleSSNVisible
              disabled={!isEmployerIsPresent}
              value={filtersValues.isSSNShowed}
              onChangeValue={handleToggleSSN}
            />
          }
          filters={
            <Box direction="row" fill>
              <Grid
                columns={
                  isPreviewMode
                    ? {
                        count: 3,
                        size: 'auto',
                      }
                    : ['220px', '220px', '220px', '220px', '220px']
                }
                gap="spacing12"
              >
                {!isPreviewMode && (
                  <SearchInput
                    testId="report"
                    placeholder="Search"
                    value={filtersValues.searchString}
                    disabled={!isEmployerIsPresent && !isMultipleEmployerAvailable}
                    onChange={onSetFilterValue.searchString}
                  />
                )}
                {isMultipleEmployerAvailable ? (
                  <MultipleEmployerSelector
                    disabled={isPreviewMode && !!defaultEmployerId}
                    onSetAutoFocus={setAutoFocus}
                    isAutoFocus={isAutoFocus}
                    values={filtersValues.employerIds}
                    onChangeValues={onSetFilterValue.employerIds}
                  />
                ) : (
                  <EmployerSelector
                    disabled={isPreviewMode && !!defaultEmployerId}
                    onSetAutoFocus={setAutoFocus}
                    isAutoFocus={isAutoFocus}
                    value={filtersValues.employerId}
                    onChangeValue={onSetFilterValue.employerId}
                  />
                )}
                <CardStatusSelector
                  values={filtersValues.cardStatuses}
                  onChangeValues={onSetFilterValue.cardStatuses}
                  disabled={!isEmployerIsPresent}
                />
                <EmploymentStatusDropdown
                  values={filtersValues.employmentStatuses}
                  onChangeValues={onSetFilterValue.employmentStatuses}
                  disabled={!isEmployerIsPresent}
                />
                {!isPreviewMode && (
                  <MailedDatePicker
                    startDate={filtersValues.mailedStartDate}
                    endDate={filtersValues.mailedEndDate}
                    onSetStartDate={onSetFilterValue.mailedStartDate}
                    onSetEndDate={onSetFilterValue.mailedEndDate}
                    disabled={!isEmployerIsPresent}
                  />
                )}
              </Grid>
            </Box>
          }
        >
          <Box>{childrenNode}</Box>
        </ReportTableWrapper>
      </TopButtonPortalWrapper>
    </Box>
  );
};
export default CardStatusReportTable;
